import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { workspaceConversationPath } from "@/features/chat/conversationShopScope";
import { merchantHomeIntentScopeKey, useCreateMerchantHomeIntentMutation, type MerchantHomeIntentInput } from "./merchantHomeApi";

type StartInput = Omit<MerchantHomeIntentInput, "idempotencyKey">;
interface StartOptions {
  strategyReturnTo?: string;
}
export function merchantHomeIntentRetryKey(input: StartInput, storage: Pick<Storage, "getItem" | "setItem"> | null) {
  const identity = JSON.stringify([input.userId, input.organizationId, input.intentType, input.shopBindingId ?? "", input.reference ?? null, input.market ?? "", input.category ?? "", ...(input.actionPurpose ? [input.actionPurpose] : [])]);
  const storageKey = `merry.home-intent.v1:${identity}`;
  try {
    const previous = storage?.getItem(storageKey);
    if (previous && /^home:[a-z0-9-]{36}$/u.test(previous)) return { idempotencyKey: previous, storageKey };
  } catch { /* 存储受限时仍使用本轮 ref 保持幂等。 */ }
  const idempotencyKey = `home:${crypto.randomUUID()}`;
  try { storage?.setItem(storageKey, idempotencyKey); } catch { /* 浏览器存储可选。 */ }
  return { idempotencyKey, storageKey };
}
export function useMerchantHomeIntent(scopeKey: string) {
  const [create, state] = useCreateMerchantHomeIntentMutation();
  const navigate = useNavigate();
  const activeGeneration = useRef<symbol | null>(null);
  const pending = useRef<symbol | null>(null);
  const retry = useRef<{ fingerprint: string; idempotencyKey: string; storageKey: string } | null>(null);
  const [failure, setFailure] = useState<{ scopeKey: string; message: string } | null>(null);
  useLayoutEffect(() => {
    const generation = Symbol(scopeKey);
    activeGeneration.current = generation;
    return () => { if (activeGeneration.current === generation) activeGeneration.current = null; };
  }, [scopeKey]);
  const start = useCallback(async (input: StartInput, options: StartOptions = {}) => {
    const generation = activeGeneration.current;
    if (!generation || pending.current === generation) return;
    pending.current = generation; setFailure(null);
    try {
      let storage: Storage | null = null;
      try { storage = window.sessionStorage; } catch { /* 存储 getter 也可能被浏览器策略拒绝。 */ }
      const fingerprint = JSON.stringify(input);
      if (retry.current?.fingerprint !== fingerprint) retry.current = { fingerprint, ...merchantHomeIntentRetryKey(input, storage) };
      const operation = retry.current;
      const result = await create({ ...input, idempotencyKey: operation.idempotencyKey }).unwrap();
      // 用户离开首页或切换身份/组织/店铺后，晚到响应只保留原操作，不再跳转旧会话。
      if (activeGeneration.current !== generation) return result;
      if (result.send_status === "sent") {
        try { storage?.removeItem(operation.storageKey); } catch { /* 无敏感数据。 */ }
        retry.current = null;
      }
      // 后端已持久化唯一可见开场，切页后只订阅会话；不能走 handleFirstSend 二次发送。
      navigate(workspaceConversationPath(result.conversation_id, input.shopBindingId ?? ""), {
        state: {
          merchantHomeIntent: {
            operationId: result.operation_id,
            sendStatus: result.send_status,
          },
          ...(options.strategyReturnTo
            ? { strategyReturnTo: options.strategyReturnTo }
            : {}),
        },
      });
      return result;
    } catch { if (activeGeneration.current === generation) setFailure({ scopeKey, message: "引导暂时未能启动，请重试；重复点击会沿用同一次请求。" }); }
    finally { if (pending.current === generation) pending.current = null; }
  }, [create, navigate, scopeKey]);
  const input = state.originalArgs;
  const sameScope = input && merchantHomeIntentScopeKey(input) === scopeKey;
  return { start, clearError: () => setFailure(null), isStarting: Boolean(state.isLoading && sameScope), error: failure?.scopeKey === scopeKey ? failure.message : "" };
}
